/**
 * Market hours utilities for SPX/SPXW.
 * All calculations in US Eastern Time.
 * Pure functions — no side effects, no state.
 *
 * All functions that need holiday awareness accept a `holidays: Set<string>`
 * parameter — a set of YYYY-MM-DD date strings. On the frontend this comes
 * from the HolidaysProvider; on the server it's queried from the database.
 */

const RTH_OPEN = 570; // 9:30 AM ET in minutes
const RTH_CLOSE = 960; // 4:00 PM ET in minutes
const OPTIONS_REGULAR_CLOSE = 975; // 4:15 PM ET in minutes
const ET_TIME_ZONE = "America/New_York";
const ET_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function utcCalendarDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

function utcCalendarDateStr(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

export type MarketSessionProfile = "us_equity_rth" | "options_regular_415";

export function marketSessionProfileForTicker(
  symbol: string | null | undefined,
): MarketSessionProfile {
  switch (symbol?.toUpperCase()) {
    case "SPX":
    case "SPXW":
    case "SPY":
    case "QQQ":
    case "IWM":
    case "NDX":
    case "RUT":
    case "VIX":
    case "VXN":
    case "RVX":
      return "options_regular_415";
    default:
      return "us_equity_rth";
  }
}

export function marketSessionLengthMs(
  profile: MarketSessionProfile = "us_equity_rth",
): number {
  const close =
    profile === "options_regular_415" ? OPTIONS_REGULAR_CLOSE : RTH_CLOSE;
  return (close - RTH_OPEN) * 60 * 1000;
}

/**
 * Find the most recent completed trading day before the given date.
 * Walks backward, skipping weekends and market holidays.
 * Returns YYYY-MM-DD string.
 */
export function previousTradingDay(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): string {
  const anchor = toET(date);
  const cursor = utcCalendarDate(anchor.year, anchor.month, anchor.day);
  for (let i = 0; i < 10; i++) {
    cursor.setUTCDate(cursor.getUTCDate() - 1);
    const dateStr = utcCalendarDateStr(cursor);
    const weekday = cursor.getUTCDay();
    if (weekday >= 1 && weekday <= 5 && !holidays.has(dateStr)) {
      return dateStr;
    }
  }
  // Fallback — shouldn't happen with 10-day lookback.
  return utcCalendarDateStr(cursor);
}

/**
 * Find the next trading day after the given date.
 * Walks forward, skipping weekends and market holidays.
 * Returns YYYY-MM-DD string.
 */
export function nextTradingDay(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): string {
  const anchor = toET(date);
  const cursor = utcCalendarDate(anchor.year, anchor.month, anchor.day);
  for (let i = 0; i < 10; i++) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const dateStr = utcCalendarDateStr(cursor);
    const weekday = cursor.getUTCDay();
    if (weekday >= 1 && weekday <= 5 && !holidays.has(dateStr)) {
      return dateStr;
    }
  }
  return utcCalendarDateStr(cursor);
}

/** Convert a Date to Eastern Time components. */
export function toET(date: Date): {
  year: number;
  month: number;
  day: number;
  weekday: number;
  hours: number;
  minutes: number;
  timeInMinutes: number;
  dateStr: string;
} {
  const parts = Object.fromEntries(
    ET_PARTS_FORMATTER.formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  const weekday = WEEKDAY_INDEX[parts.weekday] ?? 0;
  const hours = Number(parts.hour);
  const minutes = Number(parts.minute);
  return {
    year,
    month,
    day,
    weekday,
    hours,
    minutes,
    timeInMinutes: hours * 60 + minutes,
    dateStr: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
  };
}

/** Check if the market is currently in Regular Trading Hours. */
export function isRTH(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): boolean {
  const et = toET(date);
  // Weekend
  if (et.weekday === 0 || et.weekday === 6) return false;
  // Holiday
  if (holidays.has(et.dateStr)) return false;
  // Check time window
  return et.timeInMinutes >= RTH_OPEN && et.timeInMinutes < RTH_CLOSE;
}

export type MarketPhase =
  | "pre-market"
  | "rth"
  | "after-hours"
  | "weekend"
  | "holiday";

/** Get the current market phase. */
export function getMarketPhase(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): MarketPhase {
  const et = toET(date);
  if (et.weekday === 0 || et.weekday === 6) return "weekend";
  if (holidays.has(et.dateStr)) return "holiday";
  if (et.timeInMinutes < RTH_OPEN) return "pre-market";
  if (et.timeInMinutes >= RTH_CLOSE) return "after-hours";
  return "rth";
}

/**
 * Find the next market open time.
 * Returns a Date in UTC representing 9:30 AM ET on the next trading day.
 */
export function nextOpen(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): Date {
  const et = toET(date);

  // If currently before open on a trading day, open is today
  if (
    et.weekday >= 1 &&
    et.weekday <= 5 &&
    !holidays.has(et.dateStr) &&
    et.timeInMinutes < RTH_OPEN
  ) {
    return etToUTC(et.year, et.month, et.day, 9, 30);
  }

  // Otherwise, find the next trading day
  const cursor = utcCalendarDate(et.year, et.month, et.day);
  for (let i = 0; i < 10; i++) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const dateStr = utcCalendarDateStr(cursor);
    const weekday = cursor.getUTCDay();
    if (weekday >= 1 && weekday <= 5 && !holidays.has(dateStr)) {
      return etToUTC(
        cursor.getUTCFullYear(),
        cursor.getUTCMonth() + 1,
        cursor.getUTCDate(),
        9,
        30,
      );
    }
  }

  // Fallback — shouldn't happen with 10-day lookahead.
  return etToUTC(
    cursor.getUTCFullYear(),
    cursor.getUTCMonth() + 1,
    cursor.getUTCDate(),
    9,
    30,
  );
}

/**
 * 9:30 AM ET of the most recent (or current) trading session, as a UTC ms.
 * Used by intraday-chart consumers to bound the history fetch to a single
 * session — without this, /api/v1/quotes returns its full row budget,
 * which can span multiple sessions depending on ticker pairing.
 *
 * - During or after RTH on a trading day → today's 9:30 ET
 * - Pre-market on a trading day → previous trading day's 9:30 ET
 *   (so the chart still has data while waiting for today's open)
 * - Weekend / holiday → previous trading day's 9:30 ET
 */
export function currentSessionStartMs(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): number {
  const et = toET(date);
  const isTradingDay =
    et.weekday >= 1 && et.weekday <= 5 && !holidays.has(et.dateStr);
  if (isTradingDay && et.timeInMinutes >= RTH_OPEN) {
    return etToUTC(et.year, et.month, et.day, 9, 30).getTime();
  }
  const prev = previousTradingDay(date, holidays);
  const [y, m, d] = prev.split("-").map(Number);
  return etToUTC(y, m, d, 9, 30).getTime();
}

/**
 * 16:00 ET (regular session close) of the same session that
 * `currentSessionStartMs` would return — i.e. always 6h30m later.
 * Used to upper-bound the intraday filter so post-RTH ticks (legacy
 * VIX overnight prints, manual backfill) can't sneak past the 4:00 pm
 * close mark and skew the displayed percent-change after hours.
 */
export function currentSessionEndMs(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
  profile: MarketSessionProfile = "us_equity_rth",
): number {
  return currentSessionStartMs(date, holidays) + marketSessionLengthMs(profile);
}

/**
 * Start-of-range UTC ms for the eval-time scroller.
 * 9:30 AM ET of today if today is a trading day, otherwise the next trading day.
 * Unlike nextOpen, this returns *today* even during RTH or after-hours.
 */
export function currentOrNextTradingOpenMs(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): number {
  const et = toET(date);
  if (
    et.weekday >= 1 &&
    et.weekday <= 5 &&
    !holidays.has(et.dateStr) &&
    et.timeInMinutes < RTH_CLOSE
  ) {
    return etToUTC(et.year, et.month, et.day, 9, 30).getTime();
  }
  const cursor = utcCalendarDate(et.year, et.month, et.day);
  for (let i = 0; i < 10; i++) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const dateStr = utcCalendarDateStr(cursor);
    const weekday = cursor.getUTCDay();
    if (weekday >= 1 && weekday <= 5 && !holidays.has(dateStr)) {
      return etToUTC(
        cursor.getUTCFullYear(),
        cursor.getUTCMonth() + 1,
        cursor.getUTCDate(),
        9,
        30,
      ).getTime();
    }
  }
  return etToUTC(et.year, et.month, et.day, 9, 30).getTime();
}

/**
 * Bounds for the eval-time scroller:
 *  - rangeStartMs: the current moment (`date.getTime()`). The scroller's left edge is "now"
 *    so the user can scroll the cursor backward through weekend/after-hours time toward the
 *    moment the position was opened.
 *  - rangeEndMs:   4:00 PM ET of the expiry date, where "expiry date" is `dte` calendar days
 *    after `date` (today). `dte` is calendar days from today to expiry — matches the
 *    `computeT` model and `futureTradingDays`' `calendarDaysFromNow`.
 */
export function evalTimeBounds(
  dte: number,
  _holidays: Set<string> = new Set(),
  date: Date = new Date(),
): { rangeStartMs: number; rangeEndMs: number } {
  const rangeStartMs = date.getTime();
  const today = toET(date);
  const shifted = new Date(
    Date.UTC(today.year, today.month - 1, today.day + dte),
  );
  const rangeEndMs = etToUTC(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    shifted.getUTCDate(),
    16,
    0,
  ).getTime();
  return { rangeStartMs, rangeEndMs };
}

/**
 * DTE label for the eval-time scroller — number of 4 PM ET day boundaries between `cursorMs`
 * (exclusive) and `expiryCloseMs` (inclusive). DST-safe: boundaries are anchored to 4 PM ET
 * calendar days, not absolute 24-hour intervals.
 *
 * - cursor at or before expiry 4 PM on the same ET day → 0
 * - cursor strictly after 4 PM on ET day D rolls the bucket to D+1
 * - cursor at exactly 4 PM on ET day D stays on day D (higher bucket)
 */
export function dteAtCursor(cursorMs: number, expiryCloseMs: number): number {
  if (cursorMs >= expiryCloseMs) return 0;
  const cursor = toET(new Date(cursorMs));
  const expiry = toET(new Date(expiryCloseMs));
  const cursorBucketDate = new Date(
    Date.UTC(cursor.year, cursor.month - 1, cursor.day),
  );
  if (cursor.timeInMinutes > RTH_CLOSE) {
    cursorBucketDate.setUTCDate(cursorBucketDate.getUTCDate() + 1);
  }
  const expiryDate = new Date(
    Date.UTC(expiry.year, expiry.month - 1, expiry.day),
  );
  const diffMs = expiryDate.getTime() - cursorBucketDate.getTime();
  return Math.max(0, Math.round(diffMs / 86_400_000));
}

/**
 * Calendar-day offset used to identify an expiry in date pickers and filters.
 * Unlike `dteAtCursor`, this deliberately does not roll the cursor to the next
 * day after 4 PM ET: an Aug 24 expiry must remain Aug 24 when viewed on the
 * evening of Aug 23, even though its market DTE label is already 0DTE.
 */
export function calendarDteAtCursor(
  cursorMs: number,
  expiryCloseMs: number,
): number {
  const cursor = toET(new Date(cursorMs));
  const expiry = toET(new Date(expiryCloseMs));
  const cursorDate = Date.UTC(cursor.year, cursor.month - 1, cursor.day);
  const expiryDate = Date.UTC(expiry.year, expiry.month - 1, expiry.day);
  return Math.max(0, Math.round((expiryDate - cursorDate) / 86_400_000));
}

/**
 * Find the next market close time.
 * If currently in RTH, returns today's close. Otherwise, returns the next trading day's close.
 */
export function nextClose(
  date: Date = new Date(),
  holidays: Set<string> = new Set(),
): Date {
  const et = toET(date);

  // If currently in RTH, close is today
  if (isRTH(date, holidays)) {
    return etToUTC(et.year, et.month, et.day, 16, 0);
  }

  // Otherwise, close is on the next open day
  const open = nextOpen(date, holidays);
  const openET = toET(open);
  return etToUTC(openET.year, openET.month, openET.day, 16, 0);
}

/**
 * Generate the next `count` trading days starting from today (inclusive if today is a trading day
 * and market has not yet closed). Each entry's `calendarDaysFromNow` is the calendar-day offset
 * from `from` to that trading day — so on Saturday the next-expiry entry has
 * `calendarDaysFromNow = 2` (Monday), and after Friday's close it has `calendarDaysFromNow = 3`.
 * This matches the `computeT` DTE convention: DTE = calendar days from today to expiry date.
 */
export function futureTradingDays(
  count: number,
  from: Date = new Date(),
  holidays: Set<string> = new Set(),
): {
  dateStr: string;
  weekday: number;
  calendarDaysFromNow: number;
}[] {
  const today = toET(from);
  const todayDate = utcCalendarDate(today.year, today.month, today.day);
  const pastClose = today.timeInMinutes >= RTH_CLOSE;
  const result: {
    dateStr: string;
    weekday: number;
    calendarDaysFromNow: number;
  }[] = [];
  const cursor = new Date(todayDate);

  for (let i = 0; result.length < count && i < count + 60; i++) {
    const dateStr = utcCalendarDateStr(cursor);
    const weekday = cursor.getUTCDay();
    if (weekday >= 1 && weekday <= 5 && !holidays.has(dateStr)) {
      // Skip today if market has already closed — no 0-DTE expiry available
      if (i === 0 && pastClose && dateStr === today.dateStr) {
        cursor.setUTCDate(cursor.getUTCDate() + 1);
        continue;
      }
      const diffMs = cursor.getTime() - todayDate.getTime();
      const calendarDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      result.push({
        dateStr,
        weekday,
        calendarDaysFromNow: calendarDays,
      });
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return result;
}

/**
 * Generate future Fridays (trading days only) starting after `after`, out to `count` Fridays.
 * Skips Fridays that are market holidays. `calendarDaysFromNow` is anchored to `today`, so it
 * agrees with `futureTradingDays` when both are called with the same anchor. Uses the same
 * return shape as futureTradingDays.
 */
export function futureFridays(
  count: number,
  after: Date = new Date(),
  today: Date = after,
  holidays: Set<string> = new Set(),
): {
  dateStr: string;
  weekday: number;
  calendarDaysFromNow: number;
}[] {
  const afterET = toET(after);
  const afterDate = utcCalendarDate(afterET.year, afterET.month, afterET.day);
  const todayET = toET(today);
  const todayDate = utcCalendarDate(todayET.year, todayET.month, todayET.day);

  const result: {
    dateStr: string;
    weekday: number;
    calendarDaysFromNow: number;
  }[] = [];
  const cursor = new Date(afterDate);
  // Advance cursor past `after` to start searching
  cursor.setUTCDate(cursor.getUTCDate() + 1);

  for (let i = 0; result.length < count && i < 400; i++) {
    const dateStr = utcCalendarDateStr(cursor);
    const weekday = cursor.getUTCDay();
    if (weekday === 5 && !holidays.has(dateStr)) {
      const diffMs = cursor.getTime() - todayDate.getTime();
      const calendarDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      result.push({
        dateStr,
        weekday,
        calendarDaysFromNow: calendarDays,
      });
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
}

const DAILY_CALENDAR_DAYS = 30;
const MAX_CALENDAR_DAYS = 365;
export interface ListedExpiryDay {
  dateStr: string;
  calendarDaysFromNow: number;
}

const expiryDaysCache = new WeakMap<
  Set<string>,
  {
    calendarKey: string;
    holidayKey: string;
    days: ListedExpiryDay[];
  }
>();
const MAX_LISTED_EXPIRY_CALENDARS = 128;
const listedExpiryCalendarCache = new Map<string, ListedExpiryDay[]>();

function listedExpiryDaysFrom(
  from: Date,
  holidays: Set<string>,
): ListedExpiryDay[] {
  const allDaily = futureTradingDays(30, from, holidays);
  const daily = allDaily.filter(
    (day) => day.calendarDaysFromNow <= DAILY_CALENDAR_DAYS,
  );
  const lastDay = daily[daily.length - 1];
  if (!lastDay) return [];
  const lastDate = new Date(expiryCloseMs(lastDay.dateStr));
  const dailyDates = new Set(daily.map((day) => day.dateStr));
  const fridays = futureFridays(52, lastDate, from, holidays).filter(
    (friday) =>
      friday.calendarDaysFromNow <= MAX_CALENDAR_DAYS &&
      !dailyDates.has(friday.dateStr),
  );
  return [...daily, ...fridays];
}

function listedExpiryCalendarKey(from: Date, holidays: Set<string>): string {
  const anchor = toET(from);
  const marketDayPhase =
    anchor.timeInMinutes >= RTH_CLOSE ? "after-close" : "before-close";
  return `${anchor.dateStr}|${marketDayPhase}|${[...holidays].sort().join(",")}`;
}

function cachedListedExpiryDaysFrom(
  from: Date,
  holidays: Set<string>,
): ListedExpiryDay[] {
  const key = listedExpiryCalendarKey(from, holidays);
  const cached = listedExpiryCalendarCache.get(key);
  if (cached) {
    listedExpiryCalendarCache.delete(key);
    listedExpiryCalendarCache.set(key, cached);
    return cached;
  }

  const days = listedExpiryDaysFrom(from, holidays);
  listedExpiryCalendarCache.set(key, days);
  while (listedExpiryCalendarCache.size > MAX_LISTED_EXPIRY_CALENDARS) {
    const oldestKey = listedExpiryCalendarCache.keys().next().value;
    if (oldestKey === undefined) break;
    listedExpiryCalendarCache.delete(oldestKey);
  }
  return days;
}

/**
 * Compute the set of valid expiry dates: every trading day within 30 calendar days,
 * then Fridays out to 365 calendar days. Shared by ExpiryDatePicker and ExpiryCalendar.
 */
export function expiryDays(
  holidays: Set<string> = new Set(),
  from: Date = new Date(),
): ListedExpiryDay[] {
  const calendarKey = listedExpiryCalendarKey(from, holidays);
  const holidayKey = [...holidays].sort().join(",");
  const cached = expiryDaysCache.get(holidays);
  if (cached?.calendarKey === calendarKey && cached.holidayKey === holidayKey) {
    return cached.days;
  }
  const days = listedExpiryDaysFrom(from, holidays);
  expiryDaysCache.set(holidays, { calendarKey, holidayKey, days });
  return days;
}

export interface ResolvedExpiry {
  calendarDaysFromAnchor: number;
  closeMs: number;
  dateStr: string;
}

/**
 * Resolve a stored calendar-DTE selector to the actual listed expiry it
 * represents. Invalid trading dates (weekends and holidays) advance to the
 * next listed expiry once, and every pricing consumer can then share the same
 * absolute close timestamp.
 */
export function resolveExpiryForDte(
  dte: number,
  holidays: Set<string> = new Set(),
  anchor: Date = new Date(),
): ResolvedExpiry {
  const normalized = Number.isFinite(dte) ? Math.max(0, Math.round(dte)) : 0;
  const listed = cachedListedExpiryDaysFrom(anchor, holidays);
  const resolved = listed.find((day) => day.calendarDaysFromNow >= normalized);
  if (resolved) {
    return {
      calendarDaysFromAnchor: resolved.calendarDaysFromNow,
      closeMs: expiryCloseMs(resolved.dateStr),
      dateStr: resolved.dateStr,
    };
  }

  const anchorEt = toET(anchor);
  const fallback = new Date(
    Date.UTC(anchorEt.year, anchorEt.month - 1, anchorEt.day + normalized),
  );
  const dateStr = `${fallback.getUTCFullYear()}-${String(
    fallback.getUTCMonth() + 1,
  ).padStart(2, "0")}-${String(fallback.getUTCDate()).padStart(2, "0")}`;
  return {
    calendarDaysFromAnchor: normalized,
    closeMs: expiryCloseMs(dateStr),
    dateStr,
  };
}

/** Absolute 4:00 PM ET close for an ISO expiry date, DST-safe. */
export function expiryCloseMs(expiry: string): number {
  const [year, month, day] = expiry.split("-").map(Number);
  if (![year, month, day].every(Number.isFinite)) return Number.NaN;
  return etToUTC(year, month, day, 16, 0).getTime();
}

/**
 * Calendar days from `now` to the next available SPX expiry. On normal trading days pre-close
 * this is 0 (same-day expiry). On Saturday with no holidays this is 2 (Monday). After Friday's
 * close this is 3 (Monday). Matches the `computeT` DTE convention so the simulator's default
 * expiry is always a positive-T scenario.
 */
export function nextExpiryDTE(
  now: Date = new Date(),
  holidays: Set<string> = new Set(),
): number {
  const [first] = futureTradingDays(1, now, holidays);
  return first ? first.calendarDaysFromNow : 0;
}

/** Convert ET date components to a UTC Date. */
export function etToUTC(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
): Date {
  const desiredAsUtc = Date.UTC(year, month - 1, day, hours, minutes);
  let candidateMs = desiredAsUtc;
  // Two passes handle offset changes around DST without ever interpreting a
  // timezone-less string in the browser's local timezone.
  for (let pass = 0; pass < 2; pass++) {
    const actual = toET(new Date(candidateMs));
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hours,
      actual.minutes,
    );
    candidateMs += desiredAsUtc - actualAsUtc;
  }
  return new Date(candidateMs);
}
